import {Link} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import SpiningLoader from '@/components/common/SpinningLoader';
import type {AfriexReferralTier} from '@/types';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {formatName} from '@/utils/formatName';

import TierUpdateButton from './TierUpdateButton';

type TierSummaryProp = {
  item: AfriexReferralTier;
  isLoading?: boolean;
  isDetail?: boolean;
};

const TierSummary = ({item, isDetail, isLoading}: TierSummaryProp) => {
  const isMerchantTier = item?.type === 'merchant-aggregator';
  return isLoading ? (
    <SpiningLoader />
  ) : (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-2xl mr-3">{item.name}</h2>
        {!isDetail ? (
          <Link
            to={`/referral/tiers/${item.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-cyan-700 mr-2 hover:underline"
          >
            View Details
          </Link>
        ) : null}
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">Tier ID</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
            {item?.id}
          </p>
        </div>

        {isMerchantTier && (
          <>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Commission Type
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 capitalize dark:text-slate-100">
                {item?.commissionType || 'N/A'}
              </p>
            </div>

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Commission Amount (Referrer)
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
                {item?.commissionType === 'percentage'
                  ? `${formatValues(item?.bonusAmountPerReferrer || 0, 'rate', 2)}%`
                  : formatValues(item?.bonusAmountPerReferrer || 0, 'money')}
              </p>
            </div>
          </>
        )}

        {!isMerchantTier && (
          <>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Referrer Bonus
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
                {formatValues(item?.bonusAmountPerReferrer || 0, 'money')}
              </p>
            </div>

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Referee Bonus
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
                {formatValues(item?.bonusAmountPerReferree || 0, 'money')}
              </p>
            </div>
          </>
        )}

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Cumulative Threshold
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
            {formatValues(item?.cumulativeThreshold || 0, 'money')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Transaction Window
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
            {item?.transactionWindow || 0} days
          </p>
        </div>

        {!isMerchantTier && (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="text-slate-500 text-sm dark:text-slate-100">
              Referral Code
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
              {item?.referralCode || 'N/A'}
            </p>
          </div>
        )}

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Automatic Payout
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
            {item?.shouldAutomatePayout ? (
              <span className="text-green-500">Enabled</span>
            ) : (
              <span className="text-red-500">Disabled</span>
            )}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">Status</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
            {item?.isDeactivated ? (
              <span className="text-red-500">Inactive</span>
            ) : (
              <span className="text-green-500">Active</span>
            )}
          </p>
        </div>

        {item.admin && (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="text-slate-500 text-sm dark:text-slate-100">
              Created By
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
              {formatName(item.admin)}
            </p>
          </div>
        )}

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-slate-100">
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5 dark:text-slate-100">
        <div className="flex justify-end gap-4">
          <PermissionsProvider
            permission={AfriexAdminPermission.ReferralTierUpdate}
          >
            <TierUpdateButton initialData={item} />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default TierSummary;
